// 문서함 — 대화 스냅샷을 스캔해 생성 문서(보고서·엑셀)를 한곳에 모아 보여준다.
// 바닐라 app.js openLibrary/renderLibrary/libGrid 클릭 위임 이식 (Phase 3-B).
// 마크업 클래스는 바닐라와 동일(components.css 이식본 chat.css 재사용).
import { useEffect, useMemo, useState } from "react";
import { syncLabFiles } from "../../lib/labFiles";
import {
  buildLibraryIndex,
  libDownload,
  libExcelViewUrl,
  libPreviewable,
  libViewInTab,
  type LibItem,
} from "../../lib/library";
import type { VaSession } from "../../stores/chat";

interface Props {
  sessions: VaSession[];
  /** 원본 채팅으로 이동 (문서함 닫기 포함 — ChatPage 가 처리) */
  onOpenChat: (convId: string) => void;
  /** '보기' — 우측 미리보기 패널 오픈 (문서함 닫기 포함 — ChatPage 가 처리).
   *  미지원 형식은 이 콜백 대신 기존 새 탭 보기를 유지한다. */
  onPreview?: (f: { url: string; name: string; kind: string }) => void;
}

const ICON_EYE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const ICON_DL = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);
const ICON_CHAT = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M8 9h8" />
    <path d="M8 13h5" />
  </svg>
);
const ICON_GRID = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const ICON_LIST = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" />
    <path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" />
  </svg>
);
const ICON_FOLDER_OPEN = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 14l1.5-4.5A2 2 0 0 1 9.4 8H21a1 1 0 0 1 1 1.3L20.2 16a2 2 0 0 1-1.9 1.4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h7" />
  </svg>
);

function tileOf(it: LibItem): { text: string; kind: string } {
  // 형식 타일 — 인덱서가 판별한 kind/ext 사용 (라벨에 확장자가 없어도 정확)
  if (it.kind === "excel" || ["XLSX", "XLS", "CSV"].includes(it.ext))
    return { text: "X", kind: "excel" };
  if (it.kind === "report" || it.ext === "PDF") return { text: "PDF", kind: "report" };
  if (it.kind === "ppt" || ["PPTX", "PPT"].includes(it.ext)) return { text: "PPT", kind: "ppt" };
  return { text: it.ext !== "기타" ? it.ext.slice(0, 4) : "DOC", kind: "file" };
}

export default function LibraryView({ sessions, onOpenChat, onPreview }: Props) {
  // '보기' 동작 — 미리보기 지원 형식(PDF·엑셀·CSV)은 우측 패널, 그 외는 기존 새 탭.
  // lab 산출물(버핏 등)도 채팅 파일과 똑같이 미리보기가 뜬다 — 결과 화면 복귀는
  // 옆의 '원본으로 이동' 버튼(convId=딥링크)이 담당한다.
  const viewItem = (it: LibItem) => {
    if (onPreview && libPreviewable(it.kind)) {
      onPreview({ url: it.url, name: it.label || it.title, kind: it.kind });
      return;
    }
    if (it.kind === "excel") window.open(libExcelViewUrl(it.url), "_blank", "noopener");
    else libViewInTab(it.url);
  };
  // 인덱스는 열릴 때 한 번 — sessions 가 바뀌면(저장 등) 다시 만든다. 실험실 파일 기록은 서버(계정별)와 맞춘 뒤 한 번 더
  const [labSync, setLabSync] = useState(0);
  useEffect(() => {
    let active = true;
    void syncLabFiles().then(() => { if (active) setLabSync((n) => n + 1); });
    return () => { active = false; };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => buildLibraryIndex(sessions), [sessions, labSync]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"grid" | "list">(() => {
    try {
      return localStorage.getItem("va_lib_view") === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });

  // 필터 = 실제 존재하는 파일 확장자만 동적 노출
  const exts = useMemo(() => Array.from(new Set(items.map((it) => it.ext))).sort(), [items]);
  const activeFilter = filter !== "all" && !exts.includes(filter) ? "all" : filter;
  const q = query.trim().toLowerCase();
  const shown = items.filter(
    (it) =>
      (activeFilter === "all" || it.ext === activeFilter) &&
      (!q || (it.label + " " + it.company + " " + it.convTitle).toLowerCase().includes(q)),
  );

  const pickView = (v: "grid" | "list") => {
    setView(v);
    try {
      localStorage.setItem("va_lib_view", v);
    } catch {
      /* noop */
    }
  };

  return (
    <section className="library-view" aria-label="문서함">
      <div className="lib-head">
        <div className="lib-title-row">
          <h2 className="lib-title">문서함</h2>
          <span className="lib-count">{shown.length ? `${shown.length}개 문서` : ""}</span>
        </div>
        <div className="lib-tools">
          <div className="lib-filters">
            {["all", ...exts].map((x) => (
              <button
                key={x}
                type="button"
                className={`lib-filter${activeFilter === x ? " active" : ""}`}
                onClick={() => setFilter(x)}
              >
                {x === "all" ? "전체" : x}
              </button>
            ))}
          </div>
          <input
            className="lib-search"
            type="search"
            placeholder="기업명·문서명 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="lib-views" role="group" aria-label="보기 방식">
            <button
              type="button"
              className={`lib-view${view === "grid" ? " active" : ""}`}
              title="썸네일 보기"
              aria-label="썸네일 보기"
              onClick={() => pickView("grid")}
            >
              {ICON_GRID}
            </button>
            <button
              type="button"
              className={`lib-view${view === "list" ? " active" : ""}`}
              title="리스트 보기"
              aria-label="리스트 보기"
              onClick={() => pickView("list")}
            >
              {ICON_LIST}
            </button>
          </div>
        </div>
      </div>
      <div className={`lib-grid${view === "list" ? " list-mode" : ""}`}>
        {shown.map((it) => {
          const tile = tileOf(it);
          const pv = (it.preview || []).slice(0, 4);
          return (
            <article className={`lib-card ${it.kind}`} key={it.url}>
              {/* 경로 3가지: 카드 클릭=웹에서 바로 보기 / 푸터 아이콘=보기·다운로드·원본 채팅 */}
              <a
                className="lib-main"
                href={it.url}
                target="_blank"
                rel="noopener"
                title={`${it.label} — 클릭하면 미리보기가 열립니다`}
                onClick={(e) => {
                  e.preventDefault();
                  viewItem(it);
                }}
              >
                <div className="lib-preview">
                  <div className="lib-page">
                    <div className="lib-page-title">{pv[0] || it.title}</div>
                    {pv.slice(1).map((l, i) => (
                      <div className="lib-page-line" key={i}>
                        {l}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lib-body">
                  <div className="lib-name">{it.title}</div>
                </div>
                <div className="lib-foot">
                  <span className={`lib-tile ${tile.kind}`}>{tile.text}</span>
                  <span className="lib-date">{it.created}</span>
                  <span className="lib-acts">
                    <button
                      className="lib-act"
                      type="button"
                      title="웹에서 바로 보기"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        viewItem(it);
                      }}
                    >
                      {ICON_EYE}
                    </button>
                    <button
                      className="lib-act"
                      type="button"
                      title="다운로드"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void libDownload(it.url, it.label || "document");
                      }}
                    >
                      {ICON_DL}
                    </button>
                    <button
                      className="lib-act"
                      type="button"
                      title={it.featureKey === "lab" ? "결과 화면으로 이동" : "원본 채팅으로 이동"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenChat(it.convId);
                      }}
                    >
                      {ICON_CHAT}
                    </button>
                  </span>
                </div>
              </a>
            </article>
          );
        })}
      </div>
      <div className="lib-empty" hidden={shown.length > 0}>
        {ICON_FOLDER_OPEN}
        <p>아직 만든 문서가 없습니다.</p>
        <p className="lib-empty-sub">채팅에서 기업 보고서나 엑셀을 만들면 여기에 모입니다.</p>
      </div>
    </section>
  );
}
